import {
  parseFulfillmentReason,
  type AmendLineInput,
  type FulfillmentOrder,
  type FulfillmentOrderCreateInput,
  type FulfillmentOrderPatch,
  type FulfillmentTemplate,
  type FulfillmentTemplateInput,
  type PickInput,
  type PickResult,
  type StockBalance,
  type StockItem,
  type WarehouseLocation,
  type HandoverMethod,
} from '../domain/fulfillment';
import {
  fulfillmentOrderSchema,
  fulfillmentPickSchema,
  fulfillmentTemplateSchema,
  stockBalanceSchema,
  stockItemSchema,
  warehouseLocationSchema,
} from '../domain/validation';
import {
  ApiError,
  ConflictError,
  DuplicateError,
  ForbiddenError,
  FulfillmentError,
  NotFoundError,
  ValidationError,
} from './errors';
import type { FulfillmentApi, FulfillmentOrderFilters } from './fulfillment-api';

const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '/api';

function redirectToSignIn(): void {
  const returnTo = `${window.location.pathname}${window.location.search}`;
  window.location.assign(`/auth/google?returnTo=${encodeURIComponent(returnTo)}`);
}

// grpc status codes as the grpc-json transcoder reports them.
const GRPC_INVALID_ARGUMENT = 3;
const GRPC_NOT_FOUND = 5;
const GRPC_ALREADY_EXISTS = 6;
const GRPC_PERMISSION_DENIED = 7;
const GRPC_FAILED_PRECONDITION = 9;
const GRPC_ABORTED = 10;
const GRPC_UNAUTHENTICATED = 16;

/** Maps an error response to the typed errors the UI handles. */
export function fulfillmentErrorFrom(status: number, body: Record<string, unknown>): ApiError {
  const message =
    typeof body.message === 'string'
      ? body.message
      : typeof body.error === 'string'
        ? body.error
        : `Request failed with status ${status}`;
  const { reason } = parseFulfillmentReason(message);
  if (reason) return new FulfillmentError(message, reason);
  switch (body.code) {
    case GRPC_NOT_FOUND:
    case 'NOT_FOUND':
      return new NotFoundError(message);
    case GRPC_ABORTED:
    case 'CONFLICT':
      return new ConflictError(message);
    case GRPC_ALREADY_EXISTS:
    case 'DUPLICATE':
      return new DuplicateError(message);
    case GRPC_PERMISSION_DENIED:
      return new ForbiddenError(message);
    case GRPC_INVALID_ARGUMENT:
    case GRPC_FAILED_PRECONDITION:
    case 'VALIDATION':
      return new ValidationError(message);
    case GRPC_UNAUTHENTICATED:
      return new ApiError(message, 'UNAUTHENTICATED');
  }
  if (status === 403) return new ForbiddenError(message);
  if (status === 409) return new ConflictError(message);
  return new ApiError(message, 'INTERNAL');
}

/** int64 fields travel as strings; dispatch dates as RFC 3339. */
function wireRequirement(requirement: FulfillmentOrderCreateInput['lines'][number]['requirement']) {
  return {
    type: requirement.type,
    label: requirement.label || undefined,
    partIds: requirement.type === 'SERIALIZED' ? requirement.partIds : [],
    stockItemId: requirement.type === 'STOCK' ? requirement.stockItemId : undefined,
    quantity: String(requirement.quantity),
  };
}

function wireLines(lines: FulfillmentOrderCreateInput['lines']) {
  return lines.map((line) => ({
    requirement: wireRequirement(line.requirement),
    locationId: line.requirement.type === 'STOCK' ? line.locationId || undefined : undefined,
  }));
}

function wireTemplate(input: FulfillmentTemplateInput) {
  return {
    name: input.name,
    description: input.description,
    active: input.active,
    requirements: input.requirements.map(wireRequirement),
  };
}

/** Talks to leatherback through the local relay (see server/). */
export class LiveFulfillmentApi implements FulfillmentApi {
  private async fetchJSON(path: string, init?: RequestInit): Promise<Record<string, unknown>> {
    let response: Response;
    try {
      response = await fetch(`${BASE_URL}${path}`, {
        ...init,
        headers: { 'content-type': 'application/json', ...(init?.headers ?? {}) },
      });
    } catch (error) {
      throw new ApiError(
        error instanceof Error ? error.message : 'Network request failed',
        'NETWORK',
      );
    }
    const body = (await response.json().catch(() => ({}))) as Record<string, unknown>;
    if (!response.ok) {
      if (response.status === 401) {
        redirectToSignIn();
        throw new ApiError('Your session expired. Redirecting to sign-in…', 'UNAUTHENTICATED');
      }
      throw fulfillmentErrorFrom(response.status, body);
    }
    return body;
  }

  /** Follows nextPageToken until a list is complete. */
  private async listAll(path: string, params: URLSearchParams, field: string): Promise<unknown[]> {
    const rows: unknown[] = [];
    params.set('pageSize', '1000');
    for (;;) {
      const raw = await this.fetchJSON(`${path}?${params.toString()}`);
      const page = raw[field];
      if (Array.isArray(page)) rows.push(...(page as unknown[]));
      const next = raw.nextPageToken;
      if (typeof next !== 'string' || !next) return rows;
      params.set('pageToken', next);
    }
  }

  async listOrders(filters?: FulfillmentOrderFilters): Promise<FulfillmentOrder[]> {
    const params = new URLSearchParams();
    for (const state of filters?.states ?? []) params.append('states', state);
    if (filters?.siteId) params.set('siteId', filters.siteId);
    if (filters?.customerId) params.set('customerId', filters.customerId);
    if (filters?.fieldWorkRequestId) params.set('fieldWorkRequestId', filters.fieldWorkRequestId);
    const rows = await this.listAll('/v1/fulfillmentOrders', params, 'fulfillmentOrders');
    return rows.map((row) => fulfillmentOrderSchema.parse(row));
  }

  async getOrder(id: string): Promise<FulfillmentOrder> {
    return fulfillmentOrderSchema.parse(
      await this.fetchJSON(`/v1/fulfillmentOrders/${encodeURIComponent(id)}`),
    );
  }

  async createOrder(input: FulfillmentOrderCreateInput): Promise<FulfillmentOrder> {
    const raw = await this.fetchJSON('/v1/fulfillmentOrders', {
      method: 'POST',
      body: JSON.stringify({
        customerId: input.customerId,
        siteId: input.siteId,
        externalReference: input.externalReference || undefined,
        fieldWorkRequestId: input.fieldWorkRequestId || undefined,
        templateId: input.templateId || undefined,
        dispatchDate: input.dispatchDate || undefined,
        notes: input.notes || undefined,
        lines: wireLines(input.lines),
      }),
    });
    return fulfillmentOrderSchema.parse(raw);
  }

  async updateOrder(patch: FulfillmentOrderPatch): Promise<FulfillmentOrder> {
    const mask: string[] = [];
    const body: Record<string, unknown> = { version: String(patch.version) };
    if (patch.externalReference !== undefined) {
      mask.push('externalReference');
      body.externalReference = patch.externalReference;
    }
    if (patch.shipmentId !== undefined) {
      mask.push('shipmentId');
      body.shipmentId = patch.shipmentId;
    }
    if (patch.dispatchDate !== undefined) {
      mask.push('dispatchDate');
      if (patch.dispatchDate) body.dispatchDate = patch.dispatchDate;
    }
    if (patch.notes !== undefined) {
      mask.push('notes');
      body.notes = patch.notes;
    }
    if (patch.lines !== undefined) {
      mask.push('lines');
      body.lines = wireLines(patch.lines);
    }
    const params = new URLSearchParams({ updateMask: mask.join(',') });
    const raw = await this.fetchJSON(
      `/v1/fulfillmentOrders/${encodeURIComponent(patch.id)}?${params.toString()}`,
      { method: 'PATCH', body: JSON.stringify(body) },
    );
    return fulfillmentOrderSchema.parse(raw);
  }

  private async orderAction(id: string, action: string, body: object): Promise<FulfillmentOrder> {
    const raw = await this.fetchJSON(`/v1/fulfillmentOrders/${encodeURIComponent(id)}:${action}`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
    return fulfillmentOrderSchema.parse(raw);
  }

  releaseOrder(id: string, version: number, allowShortfall = false): Promise<FulfillmentOrder> {
    return this.orderAction(id, 'release', {
      version: String(version),
      ...(allowShortfall ? { allowShortfall: true } : {}),
    });
  }

  async pick(input: PickInput): Promise<PickResult> {
    const raw = await this.fetchJSON(
      `/v1/fulfillmentOrders/${encodeURIComponent(input.orderId)}:pick`,
      {
        method: 'POST',
        body: JSON.stringify({
          code: input.code,
          lineId: input.lineId,
          quantity: input.quantity === undefined ? undefined : String(input.quantity),
          manualOverride: input.manualOverride ? true : undefined,
          overrideReason: input.manualOverride?.reason,
          overrideNote: input.manualOverride?.note || undefined,
          idempotencyKey: input.idempotencyKey,
        }),
      },
    );
    return {
      order: fulfillmentOrderSchema.parse(raw.fulfillmentOrder),
      pick: fulfillmentPickSchema.parse(raw.pick),
      replayed: raw.replayed === true,
    };
  }

  removePick(orderId: string, pickId: string, reason: string): Promise<FulfillmentOrder> {
    return this.orderAction(orderId, 'removePick', { pickId, reason });
  }

  amendLine(input: AmendLineInput): Promise<FulfillmentOrder> {
    return this.orderAction(input.orderId, 'amendLine', {
      version: String(input.version),
      lineId: input.lineId,
      action: input.action,
      reason: input.reason,
      quantity: input.action === 'REDUCE_QUANTITY' ? String(input.quantity) : undefined,
      requirement: input.action === 'SUBSTITUTE' ? wireRequirement(input.requirement) : undefined,
      locationId: input.action === 'SUBSTITUTE' ? input.locationId || undefined : undefined,
    });
  }

  cancelOrder(id: string, version: number, reason: string): Promise<FulfillmentOrder> {
    return this.orderAction(id, 'cancel', { version: String(version), reason });
  }

  completeOrder(
    id: string,
    version: number,
    recipient: string,
    shipmentId?: string,
    method: HandoverMethod = 'CARRIER',
  ): Promise<FulfillmentOrder> {
    return this.orderAction(id, 'handover', {
      version: String(version),
      recipient,
      shipmentId: shipmentId || undefined,
      handoverMethod: method,
    });
  }

  async listTemplates(includeInactive = false): Promise<FulfillmentTemplate[]> {
    const params = new URLSearchParams();
    if (includeInactive) params.set('includeInactive', 'true');
    const rows = await this.listAll('/v1/fulfillmentTemplates', params, 'fulfillmentTemplates');
    return rows.map((row) => fulfillmentTemplateSchema.parse(row));
  }

  async createTemplate(input: FulfillmentTemplateInput): Promise<FulfillmentTemplate> {
    const raw = await this.fetchJSON('/v1/fulfillmentTemplates', {
      method: 'POST',
      body: JSON.stringify(wireTemplate(input)),
    });
    return fulfillmentTemplateSchema.parse(raw);
  }

  async updateTemplate(
    id: string,
    version: number,
    input: FulfillmentTemplateInput,
  ): Promise<FulfillmentTemplate> {
    const raw = await this.fetchJSON(`/v1/fulfillmentTemplates/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify({ ...wireTemplate(input), version: String(version) }),
    });
    return fulfillmentTemplateSchema.parse(raw);
  }

  async listStockItems(): Promise<StockItem[]> {
    const rows = await this.listAll('/v1/stockItems', new URLSearchParams(), 'stockItems');
    return rows.map((row) => stockItemSchema.parse(row));
  }

  async listWarehouseLocations(siteId?: string): Promise<WarehouseLocation[]> {
    const params = new URLSearchParams();
    if (siteId) params.set('siteId', siteId);
    const rows = await this.listAll('/v1/warehouseLocations', params, 'warehouseLocations');
    return rows.map((row) => warehouseLocationSchema.parse(row));
  }

  async listStockBalances(stockItemId?: string): Promise<StockBalance[]> {
    const params = new URLSearchParams();
    if (stockItemId) params.set('stockItemId', stockItemId);
    const raw = await this.fetchJSON(`/v1/stockBalances?${params.toString()}`);
    const rows = Array.isArray(raw.stockBalances) ? raw.stockBalances : [];
    return rows.map((row) => stockBalanceSchema.parse(row));
  }
}

export const fulfillmentApi = new LiveFulfillmentApi();
